import { useState } from "react";
import Image from "next/legacy/image";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { FaSpotify, FaRssSquare } from "react-icons/fa";
import {
  SiBreaker,
  SiGooglepodcasts,
  SiPocketcasts,
  SiRadiopublic,
} from "react-icons/si";

import { getSpotifyToken } from "src/lib/spotify";
import Seo from "@/components/seo";

import styles from "@/styles/PodcastInformationPage.module.css";
import {PODCAST_IDS, SPOTIFY_ID_TO_SLUG_MAPPING, SLUG_TO_SPOTIFY_ID_MAPPING, SPOTIFY_ID_TO_SHARE_LINKS} from "../../lib/podcast-constants.js";

export async function getStaticPaths() {
  const paths = PODCAST_IDS.map((id) => ({
    params: {slug: SPOTIFY_ID_TO_SLUG_MAPPING[id]}
  }));
  return {paths, fallback: false};
}

export async function getStaticProps({ params }) {
  const token = await getSpotifyToken();
  try {
    const res = await axios(
      `https://api.spotify.com/v1/shows/${
        SLUG_TO_SPOTIFY_ID_MAPPING[params.slug]
      }?market=US`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    const data = await res.data;
    //   await axios("https://api.rss2json.com/v1/api.json", {
    //     params: { rss_url: "https://anchor.fm/s/4a1b8754/podcast/rss" },
    //   }).then((res) => console.log(res.data));
    return { props: { data }, revalidate: 30 };
  }
  catch (error) {
    console.error(`Failed to fetch podcast given slug "${params.slug}" and id "${SLUG_TO_SPOTIFY_ID_MAPPING[params.slug]}":`, error);
    return { notFound: true };
  }
}

function Episode({ episode }) {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  return (
    <>
      <div className="col mb-3">
        <div className="card shadow p-0" onClick={handleShow}>
          <div className="row g-0">
            <div className="col col-lg-4">
              <Image
                src={episode.images[0].url}
                width={episode.images[0].width}
                height={episode.images[0].height}
                alt={episode.name}
                className="card-img"
                layout="responsive"
              />
            </div>
            <div className="col col-lg-8">
              <div className="card-body py-2">
                <p className="card-text small text-muted mb-0">
                  <b>{episode.release_date}</b>
                </p>
                <h6 className="card-text text-white">
                  <b>{episode.name}</b>
                </h6>
                <button className="btn btn-link p-0 d-none d-lg-block">View Episode</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{episode.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="px-5 mb-3">
            <Image
              src={episode.images[0].url}
              width={episode.images[0].width}
              height={episode.images[0].height}
              alt={episode.name}
            />
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: episode.html_description,
            }}
          />
          <div>
            <a
              href={episode.external_urls.spotify}
              target="_blank"
              rel="noopener noreferrer"
            >
              Listen on Spotify
            </a>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleClose}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function PodcastInformationPage({ data }) {
  return (
    <>
      <Seo title={`${data.name} | Podcasts`} />
      {/* Hero */}
      <div
        className="position-relative mb-3 shadow border-top border-bottom border-dark"
        style={{ background: "black" }}
      >
        <div style={{ position: "absolute", inset: 0, opacity: 0.35 }}>
          <Image
            src={data.images[0].url}
            alt={data.name}
            layout="fill"
            objectFit="cover"
          />
        </div>
        <div
          style={{
            backdropFilter: "blur(10px)",
          }}
        >
          <div className="container">
            <div className="px-4 py-5 text-center">
              <div className="row">
                <div className="col-lg-4">
                  <Image
                    width={data.images[0].width}
                    height={data.images[0].height}
                    src={data.images[0].url}
                    alt={data.name}
                    className="border border-dark img-thumbnail"
                  />
                </div>
                <div className="col align-self-center">
                  <h1 className="fw-bold text-white my-3">{data.name}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Actual Page */}
      <div className="container">
        <div className="row">
          <div className="col-lg-3 mb-3">
            <div className={`${styles.listen} sticky-lg-top`}>
              {/* Listen links */}
              <h4>Listen</h4>
              {Object.keys(SPOTIFY_ID_TO_SHARE_LINKS[data.id]).map((key, i) => {
                let icon, name;
                if (key === "spotify") {
                  icon = (
                    <FaSpotify style={{ fontSize: 35, color: "#1DB954" }} />
                  );
                  name = "Spotify";
                } else if (key === "google") {
                  icon = (
                    <SiGooglepodcasts
                      style={{ fontSize: 35, color: "white" }}
                    />
                  );
                  name = "Google Podcasts";
                } else if (key === "breaker") {
                  icon = <SiBreaker style={{ fontSize: 35, color: "white" }} />;
                  name = "Breaker";
                } else if (key === "pocket") {
                  icon = (
                    <SiPocketcasts style={{ fontSize: 35, color: "#f74132" }} />
                  );
                  name = "Pocket Casts";
                } else if (key === "radiopublic") {
                  icon = (
                    <SiRadiopublic style={{ fontSize: 35, color: "#cd252f" }} />
                  );
                  name = "RadioPublic";
                } else if (key === "rss") {
                  icon = (
                    <FaRssSquare style={{ fontSize: 35, color: "#fe8a4c" }} />
                  );
                  name = "RSS Feed";
                }

                return (
                  <a
                    href={SPOTIFY_ID_TO_SHARE_LINKS[data.id][key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="row mb-3"
                    key={i}
                  >
                    <div className="col-auto">{icon}</div>
                    <div className="col align-self-center">
                      <p className="mb-0">
                        <b>{name}</b>
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
          <div className="col">
            {/* Show information */}
            <h4>Description</h4>
            <p dangerouslySetInnerHTML={{ __html: data.html_description }} />
            <h4>Episodes</h4>
            <div className="row row-cols-1 row-cols-lg-2">
              {data.episodes.items.map((episode) => {
                return <Episode key={episode.id} episode={episode} />;
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PodcastInformationPage;
